import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/csr/ArrowLeft';

import { Button } from '../../design-system/primitives/Button/Button';
import styles from './ChallengeForm.module.css';

/**
 * A dead end, named honestly.
 *
 * `POST /auth/login` answers `202 enrolment_required` when an account MUST hold
 * a second factor and holds none. There is nothing this application can offer
 * that resolves it: enrolment lives at `POST /profile/mfa`, behind
 * `auth:sanctum`, and no session exists at this point in the flow. Rendering
 * the enrolment screen here would produce a 401 the user would read as "my code
 * was wrong".
 *
 * So the screen says what has happened and who can fix it, and offers the only
 * action that is real — going back. M-01b's administrator user management is
 * where the missing half belongs.
 *
 * This is deliberately NOT reachable by a user's own action: the API refuses to
 * disable a mandatory factor (`DELETE /profile/mfa` answers 409), so the only
 * way into this state is an administrator setting `mfa_required` on an account
 * that never enrolled.
 */

export interface EnrolmentRequiredNoticeProps {
  onBack: () => void;
}

export function EnrolmentRequiredNotice({ onBack }: EnrolmentRequiredNoticeProps) {
  const { t } = useTranslation();

  return (
    <main className={styles['page']} data-testid="enrolmentRequired">
      <div className={styles['card']}>
        <h1 className={styles['title']}>{t('auth.enrolmentRequired.title')}</h1>

        {/*
          `role="alert"` and not a plain paragraph: this replaces the sign-in
          form the user was looking at, and a screen-reader user who is given no
          announcement is left waiting for a screen that has already arrived.
        */}
        <p className={styles['intro']} role="alert">
          {t('auth.enrolmentRequired.body')}
        </p>

        <Button
          className={styles['submit']}
          onClick={onBack}
          icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}
        >
          {t('auth.enrolmentRequired.back')}
        </Button>
      </div>
    </main>
  );
}
